import React from 'react';
import { Row, Col, Card, Alert } from 'react-bootstrap';
import { AggregationTable } from './AggregationTable';
import { AggregationChart } from './AggregationChart';
import { PivotTableChartProps } from './types';
import { buildPalette, colorFor } from './color-palette';
import { useTranslation } from 'react-i18next';
import './pivot-components.css';

export const PivotTableChart: React.FC<PivotTableChartProps> = ({
  data,
  xLabel,
  measure,
  layout = 'side',
  loading = false,
  error,
}) => {
  const { t } = useTranslation();

  const MAX_BARS = 10; // keep in sync with AggregationChart prop

  const legendItems = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const numeric = data
      .filter((d) => d && d.xValueId !== 'TOTAL')
      .map((d) => ({ ...d, yValue: Number(d.yValue) || 0 }));

    const sorted = [...numeric].sort((a, b) => b.yValue - a.yValue).slice(0, MAX_BARS);
    const palette = buildPalette(Math.max(12, sorted.length));

    return sorted.map((i) => ({
      key: String(i.xValueId),
      name: i.xValueName,
      color: colorFor(i.xValueId, sorted.length, palette),
    }));
  }, [data]);
  // Loading
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Φόρτωση...</span>
        </div>
        <div className="mt-2">Φόρτωση δεδομένων...</div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Σφάλμα</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  // Empty
  if (!data || data.length === 0) {
    return (
      <Alert variant="info">
        <Alert.Heading>Δεν βρέθηκαν δεδομένα</Alert.Heading>
        <p>Δεν υπάρχουν δεδομένα για την επιλεγμένη περίοδο και κριτήρια.</p>
      </Alert>
    );
  }

  const chartComponent = (
    <Card className="h-100">
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <h6 className="mb-0">{t('Γράφημα')}</h6>
        </div>
        <div className="legend-chips mt-2" role="list" aria-label={t('Υπόμνημα')}>
          {legendItems.map((item) => (
            <div key={item.key} className="legend-chip" role="listitem">
              <span className="legend-swatch" style={{ background: item.color }} />
              <span className="legend-label">{item.name}</span>
            </div>
          ))}
        </div>
      </Card.Header>
      <Card.Body>
        <AggregationChart
          data={data}
          xLabel={xLabel}
          measure={measure}
          maxBars={MAX_BARS}
          orientation="vertical"
        />
      </Card.Body>
    </Card>
  );

  const tableComponent = (
    <Card className="h-100">
      <Card.Header>
        <h6 className="mb-0">Πίνακας Δεδομένων</h6>
      </Card.Header>
      <Card.Body className="p-0">
        <AggregationTable data={data} xLabel={xLabel} measure={measure} showTotal={true} />
      </Card.Body>
    </Card>
  );

  // Side-by-side: chart first (left), table second (right)
  if (layout === 'side') {
    return (
      <div className="pivot-table-chart">
        <Row className="h-100">
          <Col xl={6} lg={12} className="mb-3">
            {chartComponent}
          </Col>
          <Col xl={6} lg={12} className="mb-3">
            {tableComponent}
          </Col>
        </Row>
      </div>
    );
  }

  // Stacked: table first, then chart
  return (
    <div className="pivot-table-chart">
      <div className="mb-3">{tableComponent}</div>
      <div className="mb-4">{chartComponent}</div>
    </div>
  );
};
